import React, { useRef, useState, useEffect, useCallback, memo } from 'react';
import { fromJS, List } from 'immutable';
import classNames from 'classnames';

import Table from '../../helpers/AgGridTable';
import config from '../../../config';
import { RecipientsHeader } from '../RecipientsHeader';
import { InlineInput } from '../../helpers/Input';
import TextEditor from '../TextEditor';
import { GeneralFormat } from '../GeneralFormat';
import { ACTION_DELETE } from '../../helpers/DeleteItem';
import { Card } from '../../helpers/Card';
import Button from '../../helpers/Button';

import style from './style.module.css';

const FootSectionComponent = ({ sending, onGenerateLink, onGenerateEmailLink, emailLink }) => {
  const [recipients, setRecipients] = useState([]);
  const [expiresAt, setExpiresAt] = useState(null);

  const wrapperRef = useRef(null);
  const apiRef = useRef(null);

  const handleGridReady = useCallback(params => {
    apiRef.current = params.api;
    apiRef.current.sizeColumnsToFit();
  }, []);

  const updateRecipients = useCallback(
    updater => {
      setRecipients(prevRecipients => {
        const nextRecipients = updater(prevRecipients);

        onGenerateEmailLink(nextRecipients.map(recipient => recipient.email));

        return nextRecipients;
      });
    },
    [onGenerateEmailLink],
  );

  const handleInsert = useCallback(() => {
    if (!apiRef.current) return;

    apiRef.current.stopEditing();

    let rowIndex = -1;

    apiRef.current.forEachNode(rowNode => {
      if (rowNode.data.id === 0) {
        rowIndex = rowNode.rowIndex;

        return false;
      }
    });

    if (rowIndex === -1) {
      const transaction = apiRef.current.applyTransaction({
        add: [
          {
            id: 0,
            email: '',
          },
        ],
      });

      rowIndex = transaction.add[0].rowIndex;
    }

    apiRef.current.startEditingCell({ rowIndex, colKey: 'email' });
  }, []);

  const columns = config.tables.getIn(['approval', 'recipients']).map(column =>
    column.merge({
      editable: true,
      headerComponentFramework: RecipientsHeader,
      cellRendererFramework: GeneralFormat,
      cellEditorFramework: TextEditor,
      onClick: handleInsert,
    }),
  );

  const getRowNodeId = data => data.id;

  const handleCellClicked = useCallback(cell => {
    const {
      column: { colId },
      node: { rowIndex },
    } = cell;

    apiRef.current.startEditingCell({ rowIndex, colKey: colId });
  }, []);

  const handleRowEditingStopped = useCallback(
    ({ data }) => {
      updateRecipients(prevRecipients => {
        if (data.id > 0) {
          return prevRecipients.map(recipient => (data.id === recipient.id ? data : recipient));
        }

        if (data.id === 0 && data.email) {
          return [...prevRecipients, { ...data, id: prevRecipients.length + 1 }];
        }

        return prevRecipients;
      });
    },
    [updateRecipients],
  );

  const handleClickOutside = useCallback(event => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      if (apiRef.current) {
        if (!/react-datepicker/.test(event.target.className) && event.target.className !== '') {
          apiRef.current.stopEditing();
        }
      }
    }
  }, []);

  const handleContextMenuClick = useCallback(
    (event, data) => {
      const { id } = data.rowData;

      updateRecipients(prevRecipients => prevRecipients.filter(recipient => id !== recipient.id));
    },
    [updateRecipients],
  );

  const handleGenerateLink = useCallback(() => {
    apiRef.current.stopEditing();

    setTimeout(() => {
      const linkData = {
        expiresAt: expiresAt.toLocaleString(),
      };

      onGenerateLink(linkData);
    }, 100);
  }, [expiresAt, onGenerateLink]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const data = List(fromJS(recipients));
  const isDisabledLink = recipients.length === 0 || sending;

  const actions = (
    <>
      <Button disabled={expiresAt === null || sending} onClick={handleGenerateLink}>
        Generate Link
      </Button>
      <a
        className={classNames(style.emailButton, 'btn btn-primary btn-xs')}
        disabled={isDisabledLink}
        href={isDisabledLink ? null : emailLink}
        rel="noopener noreferrer"
        target="_blank"
      >
        Generate Email
      </a>
    </>
  );

  return (
    <Card actions={actions} className={style.host} title="Send to Client">
      <div className={style.body}>
        <div ref={wrapperRef} className={style.recipients}>
          <Table
            columnDefs={columns}
            contextActionList={ACTION_DELETE}
            editType="fullRow"
            getRowNodeId={getRowNodeId}
            onCellClicked={handleCellClicked}
            onContextMenuClick={handleContextMenuClick}
            onGridReady={handleGridReady}
            onRowEditingStopped={handleRowEditingStopped}
            rowData={data}
            sortable={false}
          />
        </div>
        <InlineInput
          label="Pass Exp Date"
          name="expiresAt"
          onChange={event => setExpiresAt(event.target.value)}
          placeholder="Date"
          type="date"
          value={expiresAt}
          showLabel
        />
      </div>
    </Card>
  );
};

export const FootSection = memo(FootSectionComponent);
